import { useCallback } from "react";

import { useErrorContext } from "../components/error-boundary/context";
import { useAppContext } from "./useAppContext";

export const useErrorHandling = () => {
	const {
		editor: { logger, client },
	} = useAppContext();
	const { setBlockingError } = useErrorContext() || {};

	function isOperationAborted(error: unknown) {
		return (
			error instanceof eXpress.OperationAbortedException ||
			error instanceof eXpress.OperationAbortedWarning
		);
	}

	const createErrorHandler = useCallback(
		({
			meta,
			logErrorToConsole = true,
			logErrorToServer = true,
			shouldReThrow = false,
			onErrorId,
		}: {
			/** Extra error information, useful to debug */
			meta?: string;
			/** Default: true */
			logErrorToConsole?: boolean;

			/** Default: true */
			logErrorToServer?: boolean;

			/** Default: false */
			shouldReThrow?: boolean;

			/**
			 * Callback when error is logged to the server
			 * @param errorId undefined: loading, "error": error logging, else: errorId
			 */
			onErrorId?(errorId: string | undefined, error: unknown): void;
		} = {}) =>
			function errorHandler(error: unknown) {
				if (isOperationAborted(error)) {
					return;
				}

				// Log to console
				if (logErrorToConsole) {
					if (error instanceof eXpress.Warning) {
						logger.warn(`${meta ? `${meta}: ` : ""}${eXpress.Exception.toJson(error)}`);
					} else {
						logger.error(
							`${meta ? `${meta}: ` : ""}${eXpress.Exception.toJson(error)}`,
						);
					}
				}

				// Log to server
				if (client && logErrorToServer) {
					client
						.logErrorAsync(error, true)
						.then((errorId) => {
							if (onErrorId) onErrorId(errorId, error);
						})
						.catch((logError: unknown) => {
							logger.error(
								`Error loging to server: ${eXpress.Exception.getMessages(
									logError,
								).join(",")}`,
							);
							if (onErrorId) onErrorId("error", error);
						});
				}

				if (shouldReThrow) throw error;
			},
		[logger, client],
	);

	const createFatalErrorHandler = useCallback(
		({
			onErrorId,
		}: {
			/**
			 * Callback when error is logged to the server
			 * @param errorId undefined: loading, "error": error logging, else: errorId
			 */
			onErrorId?(errorId: string | undefined, error: unknown): void;
		} = {}) => {
			const errorHandler = createErrorHandler({ onErrorId });
			return function fatalErrorHandler(error: unknown) {
				if (isOperationAborted(error)) {
					return;
				}

				if (setBlockingError) {
					// show blocking Error
					setBlockingError(error);
				}

				// the usEffect in errorBoudary.ts is not called in time (because of a changed blockingError) to update the onErrorId.
				// Adding a timeout of 1ms does the trick..
				setTimeout(() => errorHandler(error), 1);

				//  Do default error handling
			};
		},
		[createErrorHandler, setBlockingError],
	);

	return {
		createErrorHandler,
		createFatalErrorHandler,
	};
};
